import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reliable scroll-to-top on route change.
 *
 * Why so many guards?
 *   The admin / agent / promo panels render long pages whose content arrives
 *   asynchronously (API calls finish after the first paint).  A simple
 *   `window.scrollTo(0, 0)` in a `useEffect` runs ONCE — before the data has
 *   landed — so the page first jumps to top, then a later component re-mount
 *   or layout-shift can push the scroll back down again on slow networks.
 *
 *   This component handles every case:
 *     1. Synchronous (`useLayoutEffect`) — before paint
 *     2. Next animation frame — after the new route's first paint
 *     3. Three timed retries (60ms / 220ms / 500ms) — catches API-driven
 *        late layout-shifts
 *     4. Resets EVERY scrollable container, not just the window
 *     5. Disables browser scroll-restoration on mount so back/forward
 *        navigation doesn't fight us
 *     6. Also re-fires on `hashchange` so in-page anchor links scroll up too
 */
export default function ScrollToTop() {
  const { pathname, search } = useLocation();
  const navTokenRef = useRef("");

  const doScroll = () => {
    // 1. Window
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* ignored */
      }
    }
    // 2. Document elements (Safari / older Chrome)
    try { document.documentElement.scrollTop = 0; } catch { /* ignored */ }
    try { document.body.scrollTop = 0; } catch { /* ignored */ }

    // 3. Reset every internal scrollable container (admin/agent shells use them
    //    for tables, modals, side-drawers, panels with sticky topbars).
    try {
      const candidates = document.querySelectorAll(
        "[data-scroll-root], main, .overflow-y-auto, .overflow-auto, .overflow-y-scroll",
      );
      candidates.forEach((el) => {
        if (el.hasAttribute("data-scroll-keep")) return;
        // Skip the sidebar's own nav scroll — that one should preserve its
        // position so the user doesn't lose their place when navigating.
        if (el.closest("[data-testid='sidebar']")) return;
        try { el.scrollTop = 0; } catch { /* ignored */ }
      });
    } catch {
      /* ignored */
    }

    // 4. Legacy id targets (kept for backward-compat with older shells)
    ["main-scroll", "app-scroll", "admin-content", "agent-content"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        try { el.scrollTop = 0; } catch { /* ignored */ }
      }
    });
  };

  useLayoutEffect(() => {
    const token = `${pathname}?${search}`;
    navTokenRef.current = token;

    // Pass 1: synchronous, before paint
    doScroll();

    // Pass 2: next animation frame, after first paint
    const raf = requestAnimationFrame(() => {
      if (navTokenRef.current === token) doScroll();
    });

    // Pass 3-5: catch async content + layout shifts (60ms / 220ms / 500ms)
    const timers = [60, 220, 500].map((ms) =>
      setTimeout(() => {
        if (navTokenRef.current === token) doScroll();
      }, ms),
    );

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [pathname, search]);

  // One-time setup: disable browser scroll-restoration so back/forward navs
  // don't compete with our explicit scroll.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // Bonus: scroll to top when the URL hash is cleared / changed to empty
  // (some anchor-style nav links toggle a hash like #/ which the router
  // doesn't always treat as a pathname change).
  useEffect(() => {
    const onHash = () => {
      if (!window.location.hash || window.location.hash === "#") {
        doScroll();
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  return null;
}
